import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/shared/lib/auth-context';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { RecsysSidebar } from './components/RecsysSidebar';
import { ProfileDeleteDialog } from './components/ProfileDeleteDialog';
import { LaunchErrorBanner } from './components/LaunchErrorBanner';
import { ProfileWorkspaceHeader } from './components/ProfileWorkspaceHeader';
import { ProfileWorkspaceBody } from './components/ProfileWorkspaceBody';
import { MeetingBottomPanel } from './components/MeetingBottomPanel';
import { WelcomeEmptyState } from './components/WelcomeEmptyState';
import type { DemoMode } from './components/DemoModeSwitcher';
import type { Profile, ProfileStatus } from './types';
import { useProfileManager } from './hooks/useProfileManager';
import { usePinnedProfiles } from './hooks/usePinnedProfiles';
import { useProfileTitle } from './hooks/useProfileTitle';
import { useProfileForms } from './hooks/useProfileForms';
import { useServiceFlags } from '@/shared/api/useFeatures';
import { useVisitCard } from './hooks/useVisitCard';
import { useMeetingSession } from './hooks/useMeetingSession';
import { usePartnerLaunch } from './hooks/usePartnerLaunch';
import { telephoneFromOpenProfile } from './formState';
import {
  revealTourHeldProfiles,
  TourOverlay,
  useDemoTour,
  useDemoMeetingPlayback,
  type TourWorld,
} from './tour';

export default function RecsysPage() {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const username = auth.user?.username ?? '';
  const {
    profiles, selectedProfileId, isLoadingProfiles, hasRestoredSelection, profilesError, profileMetaById,
    selectedMeta, selectedProfileTitle, onSelectProfile, onCreateProfile, deleteProfile,
  } = useProfileManager();
  const {
    isPinned,
    togglePinned,
    removePinned,
    sortProfilesWithPins,
  } = usePinnedProfiles(username);
  const title = useProfileTitle(selectedProfileId, selectedProfileTitle);
  const { startCreateForProfile, updatePresentatorFormItem, setMeta } = useProfileForms();
  const featureFlags = useServiceFlags();
  const visitCard = useVisitCard(featureFlags.visit_card);
  const meeting = useMeetingSession({
    selectedProfileId,
    selectedProfileTitle,
    telephone: telephoneFromOpenProfile(selectedMeta),
    profileReady:
      selectedMeta?.status === 'success' && Boolean(selectedMeta.profileData),
    visitCard: {
      init: visitCard.init,
      reset: visitCard.reset,
      onVisitCardUpdate: visitCard.onVisitCardUpdate,
    },
    featureFlags: { copilot: featureFlags.copilot, visit_card: featureFlags.visit_card },
  });

  const [demoMode, setDemoMode] = React.useState<DemoMode>('tour');
  const [scrollToRecordId, setScrollToRecordId] = React.useState<string | null>(null);
  const addLocalDemoRecord = meeting.addLocalDemoRecord;
  const handlePersistDemoRecord = React.useCallback(
    ({ durationSec }: { durationSec: number }) => {
      const localId = addLocalDemoRecord({ durationSec });
      if (!localId) return;
      setScrollToRecordId(`local-${localId}`);
    },
    [addLocalDemoRecord],
  );

  // Playback hook needs tour step for arming; tour needs playback for pause.
  // Mirror isPlaying in state so tour can pause without a circular hook order.
  const [demoPlaying, setDemoPlaying] = React.useState(false);

  const localRecordsForSelected = selectedProfileId
    ? (meeting.localAudioByProfileId[selectedProfileId] ?? [])
    : [];
  // Only real records count. keepPanel leftover is the Copilot overlay, not
  // the meeting-results card — treating it as results snaps the tour onto a
  // missing target after skip-during-recording, so hints never appear.
  const hasMeetingResults = Boolean(
    (selectedMeta?.profileData?.records?.length ?? 0) > 0 ||
      localRecordsForSelected.length > 0,
  );
  const tourWorld = React.useMemo<TourWorld>(
    () => ({
      hasSelectedProfile: Boolean(selectedProfileId),
      hasProfileContent:
        selectedMeta?.status === 'success' && Boolean(selectedMeta.profileData),
      hasMeetingResults,
      isPlaybackActive: demoPlaying,
    }),
    [
      selectedProfileId,
      selectedMeta?.status,
      selectedMeta?.profileData,
      hasMeetingResults,
      demoPlaying,
    ],
  );

  const revealHeldProfiles = React.useCallback(() => {
    void revealTourHeldProfiles(queryClient);
  }, [queryClient]);

  const tour = useDemoTour(demoMode, setDemoMode, {
    isPlaybackActive: demoPlaying,
    username,
    world: tourWorld,
    worldReady:
      hasRestoredSelection &&
      !(selectedProfileId && selectedMeta?.isLoadingProfileData),
    revealHeldProfiles,
  });
  const demoPlayback = useDemoMeetingPlayback({
    tourStepId: tour.currentStep?.id ?? null,
    isTourRunning: tour.isRunning,
    appendRecommendations: meeting.appendRecommendations,
    applyCheckpoints: meeting.applyCheckpoints,
    resetCheckpointsForDemo: meeting.resetCheckpointsForDemo,
    markMissedCheckpoints: meeting.markMissedCheckpoints,
    onPersistRecord: handlePersistDemoRecord,
  });
  React.useEffect(() => {
    setDemoPlaying(demoPlayback.isPlaying);
  }, [demoPlayback.isPlaying]);

  const handleSkipTour = React.useCallback(() => {
    demoPlayback.stop({ persist: false });
    demoPlayback.dismissPanel();
    tour.skipTour();
  }, [demoPlayback, tour]);

  const handleDemoModeChange = React.useCallback(
    (mode: DemoMode) => {
      if (mode === 'free') {
        demoPlayback.stop({ persist: false });
        demoPlayback.dismissPanel();
      }
      tour.onDemoModeChange(mode);
    },
    [demoPlayback, tour],
  );

  const [deletingProfileId, setDeletingProfileId] = React.useState<string | null>(null);
  const canAddViaModal = selectedMeta?.status !== 'not_started';
  const sidebarProfiles = React.useMemo(
    () =>
      profiles.map((p) => ({
        ...p,
        status: profileMetaById[p.id]?.status,
      })) as (Profile & { status: ProfileStatus })[],
    [profiles, profileMetaById],
  );

  const handleDeleteProfile = React.useCallback(
    async (id: string) => {
      removePinned(id);
      await deleteProfile(id);
    },
    [deleteProfile, removePinned],
  );

  const meetingLive =
    meeting.isRecording ||
    meeting.isCopilotActive ||
    demoPlayback.isPlaying;
  const meetingLeftover =
    meeting.keepPanelAfterStop || demoPlayback.keepPanel;

  const handleSelectProfile = React.useCallback(
    (id: string) => {
      // Deselect would unmount the recorder mid-meeting — block while live.
      if (meetingLive && id === selectedProfileId) return;

      const leavingMeetingLeftover =
        meetingLeftover &&
        !meetingLive &&
        meeting.meetingProfileId != null &&
        selectedProfileId === meeting.meetingProfileId &&
        id !== meeting.meetingProfileId;

      meeting.handleProfileSwitch(id, {
        isLive: meetingLive,
        hasLeftover: meetingLeftover,
      });
      if (leavingMeetingLeftover) demoPlayback.dismissPanel();
      onSelectProfile(id);
    },
    [
      demoPlayback,
      meeting,
      meetingLeftover,
      meetingLive,
      onSelectProfile,
      selectedProfileId,
    ],
  );

  const handleCreateProfile = React.useCallback(() => {
    const leavingMeetingLeftover =
      meetingLeftover &&
      !meetingLive &&
      meeting.meetingProfileId != null &&
      selectedProfileId === meeting.meetingProfileId;

    // Creating always leaves the current profile for a new id.
    meeting.handleProfileSwitch('', {
      isLive: meetingLive,
      hasLeftover: meetingLeftover,
    });
    if (leavingMeetingLeftover) demoPlayback.dismissPanel();
    return onCreateProfile();
  }, [
    demoPlayback,
    meeting,
    meetingLeftover,
    meetingLive,
    onCreateProfile,
    selectedProfileId,
  ]);

  const partner = usePartnerLaunch({
    isAuthenticated: auth.isAuthenticated,
    isLoadingProfiles,
    copilotEnabled: featureFlags.copilot,
    isRecording: meeting.isRecording,
    onCreateProfile: handleCreateProfile,
    startCreateForProfile,
  });

  return (
    <div className="h-full">
      {partner.launchError && (
        <LaunchErrorBanner
          message={partner.launchError}
          onDismiss={() => partner.setLaunchError(null)}
        />
      )}
      <SidebarProvider
        defaultOpen
        className="h-full"
        style={{ '--sidebar-width': '360px', '--sidebar-width-icon': '60px' } as React.CSSProperties}
      >
        <div className="flex h-full w-full">
          <RecsysSidebar
            profiles={sidebarProfiles}
            selectedProfileId={selectedProfileId}
            onSelectProfile={handleSelectProfile}
            onCreateProfile={handleCreateProfile}
            onDeleteProfile={handleDeleteProfile}
            isPinned={isPinned}
            onTogglePinned={togglePinned}
            sortProfilesWithPins={sortProfilesWithPins}
            isLoadingProfiles={isLoadingProfiles}
            profilesError={profilesError}
            profileMetaById={profileMetaById}
            registerTourTarget={tour.registerTarget}
            tourActiveTarget={tour.currentStep?.target ?? null}
          />
          <SidebarInset>
            {!selectedProfileId ? (
              <WelcomeEmptyState
                demoMode={demoMode}
                onDemoModeChange={handleDemoModeChange}
                onStartTour={tour.startTour}
                onRestartTour={tour.restartTour}
                canResume={tour.canResume}
              />
            ) : (
              <div className="mx-auto w-full max-w-250 h-[calc(100vh-var(--app-shell-padding))] overflow-hidden">
                <div className="relative flex h-full min-h-0 flex-col overflow-y-auto px-8 pb-2 pt-0">
                  <ProfileWorkspaceHeader
                    selectedProfileTitle={selectedProfileTitle}
                    selectedMeta={selectedMeta}
                    canRenameProfile={Boolean(selectedMeta?.isInDatabase)}
                    canDeleteProfile={Boolean(selectedMeta?.isInDatabase)}
                    canAddViaModal={canAddViaModal}
                    title={title}
                    demoMode={demoMode}
                    onDemoModeChange={handleDemoModeChange}
                    onRequestDelete={() => setDeletingProfileId(selectedProfileId)}
                    onSubmitQuestionnaire={(telephone) => {
                      void startCreateForProfile(selectedProfileId, telephone);
                    }}
                    registerTourTarget={tour.registerTarget}
                    tourKeepAddEnabled={tour.isRunning}
                  />
                  <ProfileWorkspaceBody
                    selectedProfileId={selectedProfileId}
                    selectedMeta={selectedMeta}
                    username={username}
                    localRecords={localRecordsForSelected}
                    onUpdateFormItem={updatePresentatorFormItem}
                    onStartCreate={(telephone) =>
                      startCreateForProfile(selectedProfileId, telephone, {
                        demoTour: tour.isRunning,
                      })
                    }
                    onDismissError={() => {
                      const hasProfileData = (selectedMeta?.profileData?.forms.length ?? 0) > 0;
                      setMeta(selectedProfileId, {
                        errorMessage: null,
                        status: hasProfileData ? 'success' : 'not_started',
                      });
                    }}
                    onRenameLocalRecord={meeting.renameLocalAudioRecord}
                    onDeleteLocalRecord={meeting.deleteLocalAudioRecord}
                    onRetryUploadLocalRecord={meeting.onRetryUploadLocalRecord}
                    prefillTelephone={tour.currentStep?.autofill?.telephone}
                    registerTourTarget={tour.registerTarget}
                    scrollToRecordId={scrollToRecordId}
                  />
                  <MeetingBottomPanel
                    featureFlags={featureFlags}
                    visitCard={visitCard}
                    meeting={meeting}
                    autoStart={partner.launchAutoStart}
                    selectedProfileId={selectedProfileId}
                    selectedProfileTitle={selectedProfileTitle}
                    launchAdoptSessionIdRef={partner.launchAdoptSessionIdRef}
                    onLaunchConfirmStarted={partner.confirmStarted}
                    onLaunchFail={partner.fail}
                    registerTourTarget={tour.registerTarget}
                    demoPlayback={demoPlayback}
                  />
                </div>
              </div>
            )}
          </SidebarInset>
        </div>
      </SidebarProvider>
      <ProfileDeleteDialog
        open={deletingProfileId !== null}
        onOpenChange={(open) => { if (!open) setDeletingProfileId(null); }}
        onDelete={() => {
          if (deletingProfileId) void handleDeleteProfile(deletingProfileId);
          setDeletingProfileId(null);
        }}
        disabled={selectedMeta?.status === 'processing'}
      />
      {tour.isRunning && tour.currentStep ? (
        <TourOverlay
          step={tour.currentStep}
          stepIndex={tour.stepIndex}
          stepCount={tour.stepCount}
          targetElement={tour.targetElement}
          isWaitingForTarget={tour.isWaitingForTarget}
          onSkip={handleSkipTour}
          onNext={tour.nextStep}
        />
      ) : null}
    </div>
  );
}
